import React from "react";
import { ArrowRight, Package } from "lucide-react";
import { CARD_ASSETS } from "../../lib/assets";
import { CONFIG, primaryCTA, primaryCTATarget } from "../../lib/config";

const scrollTo = (id) => {
  const el = document.querySelector(id);
  if (el) el.scrollIntoView({ behavior: "smooth" });
};

const handlePrimary = () => {
  const t = primaryCTATarget();
  if (t.startsWith("#")) scrollTo(t);
  else window.location.assign(t);
};

// Quick fact tile — renders NOTHING when value is falsy (no "TBC" placeholder).
const StatItem = ({ label, value, testId }) => {
  if (!value) return null;
  return (
    <div data-testid={testId}>
      <p className="text-[11px] uppercase tracking-widest text-white/50 font-semibold">
        {label}
      </p>
      <p className="mt-1 font-display font-bold text-white text-lg md:text-xl">
        {value}
      </p>
    </div>
  );
};

export const WhatsInTheBox = () => {
  const { product, launch } = CONFIG;

  // Build stats only from confirmed facts; null values are dropped entirely.
  const stats = [
    { label: "Players",  value: product.playerCount, testId: "stat-players" },
    { label: "Contents", value: product.cardCount ? `${product.cardCount} cards` : null, testId: "stat-contents" },
    { label: "Age",      value: product.age, testId: "stat-age" },
    { label: "Type",     value: product.productType, testId: "stat-type" },
  ].filter((s) => s.value);

  const contents = Array.isArray(product.contents) ? product.contents : [];

  return (
    <section
      id="whats-in-the-box"
      data-testid="whats-in-the-box-section"
      className="navy-bg grain py-10 md:py-28 text-white relative overflow-hidden"
    >
      <div className="max-w-7xl mx-auto px-5 md:px-8 grid grid-cols-1 lg:grid-cols-12 gap-10 md:gap-14 items-center">
        {/* Product placeholder visual — designed so real photography drops straight in */}
        <div className="lg:col-span-6 order-2 lg:order-1">
          <div
            data-testid="product-visual"
            className="relative rounded-3xl overflow-hidden aspect-[16/10] md:aspect-[4/3.4] border border-white/10 flex items-center justify-center p-4 md:p-6"
            style={{
              background:
                "radial-gradient(ellipse at 30% 30%, rgba(245,197,66,0.14), transparent 55%), radial-gradient(ellipse at 70% 70%, rgba(232,106,44,0.14), transparent 55%), var(--pw-navy-2)",
            }}
          >
            <div className="relative w-full h-full flex items-center justify-center">
              <img
                src={CARD_ASSETS.chaosMode}
                alt="People Watching sample card artwork"
                className="absolute w-[38%] rounded-lg shadow-2xl"
                style={{ transform: "translateX(-42%) rotate(-14deg)" }}
              />
              <img
                src={CARD_ASSETS.trophy}
                alt="People Watching sample card artwork"
                className="absolute w-[40%] rounded-lg shadow-2xl z-10"
                style={{ transform: "translateY(-6%) rotate(-2deg)" }}
              />
              <img
                src={CARD_ASSETS.swapHands}
                alt="People Watching sample card artwork"
                className="absolute w-[38%] rounded-lg shadow-2xl"
                style={{ transform: "translateX(42%) rotate(12deg)" }}
              />
            </div>

            <span
              className="absolute bottom-4 right-4 text-[10px] uppercase tracking-widest px-2.5 py-1 rounded-full font-semibold"
              style={{ backgroundColor: "rgba(255,255,255,0.08)", color: "rgba(255,255,255,0.65)" }}
            >
              Placeholder · Final photography to follow
            </span>
          </div>
        </div>

        <div className="lg:col-span-6 order-1 lg:order-2">
          <p className="eyebrow mb-4" style={{ color: "var(--pw-yellow)" }}>The Game</p>
          <h2
            className="font-display font-black leading-[0.95] tracking-tighter"
            style={{ fontSize: "clamp(2rem, 5.5vw, 4.5rem)" }}
          >
            One small box.
            <br />
            <span className="yellow-stroke" style={{ color: "#fff" }}>Everyone&apos;s Favourite Pastime.</span>
          </h2>

          <p className="mt-6 text-white/75 leading-relaxed max-w-lg">
            The card game that turns the people around you into the game.
            Take it to the pub, airport, festival, campsite or holiday. If there are people around,
            there&apos;s a game.
          </p>

          {/* Quick product facts — only confirmed values render */}
          {stats.length > 0 && (
            <div
              data-testid="quick-facts"
              className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-5 rounded-2xl border p-5"
              style={{ borderColor: "rgba(255,255,255,0.12)", backgroundColor: "rgba(255,255,255,0.03)" }}
            >
              {stats.map((s) => (
                <StatItem key={s.label} {...s} />
              ))}
            </div>
          )}

          {/* Detailed What's In The Box contents list — safe to explain the 2 instruction cards here */}
          {contents.length > 0 && (
            <div
              data-testid="box-contents"
              className="mt-8 rounded-2xl border p-6"
              style={{ borderColor: "rgba(255,255,255,0.12)", backgroundColor: "rgba(255,255,255,0.03)" }}
            >
              <p className="eyebrow mb-3" style={{ color: "var(--pw-orange)" }}>What&apos;s In The Box</p>
              <ul className="space-y-2 text-white/80">
                {contents.map((line, i) => (
                  <li
                    key={i}
                    data-testid={`box-contents-item-${i}`}
                    className="flex items-start gap-3 leading-relaxed"
                  >
                    <span
                      className="mt-2 inline-block w-1.5 h-1.5 rounded-full flex-shrink-0"
                      style={{ backgroundColor: "var(--pw-yellow)" }}
                    />
                    <span>{line}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          <div className="mt-8 flex flex-wrap items-center gap-3 md:gap-4">
            <button
              onClick={handlePrimary}
              data-testid="whats-in-the-box-cta"
              className="pw-btn-primary"
            >
              {primaryCTA()} <ArrowRight className="w-4 h-4" />
            </button>
            {launch.isPreLaunch && (
              <span className="inline-flex items-center gap-2 text-white/60 text-sm">
                <Package className="w-4 h-4" /> Delivery details at launch
              </span>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default WhatsInTheBox;
